import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import './MovementTray.scss';
import { createPortal } from 'react-dom';

/**
 * HUD Move pill. In combat it shows movement left out of speed and opens a list above itself where the map's
 * movement board renders its controls (walk, climb, long jump, grapples, retries). The popover lives in a
 * fixed layer so the HUD panels cannot clip it.
 */
export default function MovementPill({ speed, summary, open, onOpenChange, onHost }) {
  const pillRef = useRef(null), popoverRef = useRef(null);
  const [anchor, setAnchor] = useState(null);
  // A stable ref callback: an inline one would report null and the element on every render.
  const setPopover = useCallback((element) => { popoverRef.current = element; onHost(element); }, [onHost]);
  const interactive = Boolean(summary);
  const visible = open && interactive;

  useLayoutEffect(() => {
    if (!visible) return undefined;
    const place = () => {
      const rect = pillRef.current?.getBoundingClientRect();
      if (rect) setAnchor({ left: Math.max(8, rect.left), bottom: Math.max(8, window.innerHeight - rect.top + 8) });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); };
  }, [visible]);

  useEffect(() => {
    if (!visible) return undefined;
    const close = (event) => {
      if (event.type === 'keydown' ? event.key === 'Escape' : !pillRef.current?.contains(event.target) && !popoverRef.current?.contains(event.target)) onOpenChange(false);
    };
    document.addEventListener('pointerdown', close, true);
    document.addEventListener('keydown', close);
    return () => { document.removeEventListener('pointerdown', close, true); document.removeEventListener('keydown', close); };
  }, [visible, onOpenChange]);

  return (
    <span ref={pillRef} className={`combat-hud-pill combat-hud-move-pill${interactive ? ' combat-hud-move-pill--interactive' : ''}`} aria-label={interactive ? undefined : 'Movement speed'}>
      {interactive ? (
        <button
          type="button"
          className="combat-hud-move-pill__button"
          aria-expanded={visible}
          aria-haspopup="dialog"
          aria-label={`Movement: ${summary.remainingFeet} of ${summary.speed} feet left. Choose how to move.`}
          title="Choose how to move"
          onClick={() => onOpenChange(!open)}
        >
          {/* Kept as short as the out-of-combat badge so the stat pills never wrap to an extra row. */}
          Move: {summary.remainingFeet}/{summary.speed} ft
        </button>
      ) : (
        <>Move: {speed} ft</>
      )}
      {createPortal(
        <div
          ref={setPopover}
          className="combat-hud-move-pill__popover"
          role="dialog"
          aria-label="Movement"
          hidden={!visible}
          style={anchor ? { left: anchor.left, bottom: anchor.bottom } : undefined}
        />,
        document.body
      )}
    </span>
  );
}
